<!-- eslint-disable vue/max-lines-per-block -->
<script setup lang="ts">
  import { endOfDay, endOfMonth, endOfYear, isAfter, isBefore, isSameDay, startOfDay, startOfMonth, startOfYear, subDays, subMonths } from 'date-fns'
  import { computed, ref } from 'vue'
  import { fmt } from '@/functions'
  import { useAppModal } from '../modal'
  import DatepickerNavigator from './datepicker-navigator.vue'
  import ViewDaysInMonth from './view-days-in-month.vue'
  import ViewMonthsInYear from './view-months-in-year.vue'

  const props = defineProps<{
    date1: Date | null,
    date2: Date | null,
    clearable?: boolean,
  }>()
  const emit = defineEmits<{
    (e: 'update:date1', value: Date | null): void,
    (e: 'update:date2', value: Date | null): void,
  }>()

  const view = ref('days')
  const month = ref<number>(new Date().getMonth())
  const year = ref<number>(new Date().getFullYear())
  const modelDate1 = ref<Date | null>(props.date1)
  const modelDate2 = ref<Date | null>(props.date2)

  const dateClicked = (dateClicked: Date) => {
    if (modelDate1.value === null || isBefore(dateClicked, modelDate1.value) || modelDate2.value !== null) {
      modelDate1.value = startOfDay(dateClicked)
      modelDate2.value = null
    } else {
      modelDate2.value = endOfDay(dateClicked)
    }
  }

  const FORMAT_DATE = 'dd MMM yyyy'
  const stringifiedDate = computed(() => {
    if (props.date1 && props.date2) {
      return fmt.date(props.date1, FORMAT_DATE) + ' - ' + fmt.date(props.date2, FORMAT_DATE)
    }
    return ''
  })
  const stringifiedModel = computed(() => {
    if (modelDate1.value && modelDate2.value) {
      return fmt.date(modelDate1.value, FORMAT_DATE) + ' - ' + fmt.date(modelDate2.value, FORMAT_DATE)
    } else if (modelDate1.value && modelDate2.value === null) {
      return fmt.date(modelDate1.value, FORMAT_DATE) + ' - '
    }
    return ''
  })

  const { modal, open, close } = useAppModal(() => {
    // Reset the date picker to the current month and year
    modelDate1.value = props.date1
    modelDate2.value = props.date2
  })
  const confirm = () => {
    if (modelDate1.value && modelDate2.value) {
      emit('update:date1', modelDate1.value)
      emit('update:date2', modelDate2.value)
    } else {
      emit('update:date1', null)
      emit('update:date2', null)
    }
    close()
  }

  const shortcutToday = () => {
    month.value = new Date().getMonth()
    year.value = new Date().getFullYear()
    modelDate1.value = new Date()
    modelDate2.value = new Date()
  }
  const shortcutThisMonth = () => {
    month.value = new Date().getMonth()
    year.value = new Date().getFullYear()
    modelDate1.value = startOfMonth(new Date())
    modelDate2.value = endOfMonth(new Date())
  }
  const shortcutThisYear = () => {
    month.value = new Date().getMonth()
    year.value = new Date().getFullYear()
    modelDate1.value = startOfYear(new Date())
    modelDate2.value = endOfYear(new Date())
  }
  const shortcutLast30Days = () => {
    month.value = subDays(new Date(), 30).getMonth()
    year.value = subDays(new Date(), 30).getFullYear()
    modelDate1.value = subDays(new Date(), 30)
    modelDate2.value = new Date()
  }
  const shortcutLast3Months = () => {
    month.value = subMonths(new Date(), 3).getMonth()
    year.value = subMonths(new Date(), 3).getFullYear()
    modelDate1.value = subMonths(new Date(), 3)
    modelDate2.value = new Date()
  }
  const shortcutLast6Months = () => {
    month.value = subMonths(new Date(), 6).getMonth()
    year.value = subMonths(new Date(), 6).getFullYear()
    modelDate1.value = subMonths(new Date(), 6)
    modelDate2.value = new Date()
  }
  const shortcutLast12Months = () => {
    month.value = subMonths(new Date(), 12).getMonth()
    year.value = subMonths(new Date(), 12).getFullYear()
    modelDate1.value = subMonths(new Date(), 12)
    modelDate2.value = new Date()
  }
  const reset = () => {
    modelDate1.value = null
    modelDate2.value = null
    month.value = new Date().getMonth()
    year.value = new Date().getFullYear()
  }
  const clearDate = () => {
    reset()
    emit('update:date1', null)
    emit('update:date2', null)
  }

  const hour1 = computed({
    get: () => modelDate1.value?.getHours() ?? 0,
    set (value: number) {
      if (modelDate1.value) {
        const a = new Date(modelDate1.value)
        a.setHours(value)
        modelDate1.value = a
      }
    },
  })
  const minute1 = computed({
    get: () => modelDate1.value?.getMinutes() ?? 0,
    set (value: number) {
      if (modelDate1.value) {
        const a = new Date(modelDate1.value)
        a.setMinutes(value)
        modelDate1.value = a
      }
    },
  })
  const hour2 = computed({
    get: () => modelDate2.value?.getHours() ?? 23,
    set (value: number) {
      if (modelDate2.value) {
        const a = new Date(modelDate2.value)
        a.setHours(value)
        modelDate2.value = a
      }
    },
  })
  const minute2 = computed({
    get: () => modelDate2.value?.getMinutes() ?? 59,
    set (value: number) {
      if (modelDate2.value) {
        const a = new Date(modelDate2.value)
        a.setMinutes(value)
        modelDate2.value = a
      }
    },
  })
</script>

<template>
  <div>
    <app-input
      label="Filter Tanggal"
      class="min-w-55"
      :model-value="stringifiedDate"
      readonly>
      <template #inline>
        <div
          class="absolute inset-0 z-0"
          @click="open()"></div>
        <app-button
          v-if="stringifiedDate && clearable"
          class="absolute z-1 right-1 top-1/2 -translate-y-1/2 text-2.5 size-5 flex-center rounded-full hover:bg-gray-100"
          @click="clearDate()">
          <fa-icon icon="i-far-xmark" />
        </app-button>
      </template>
    </app-input>

    <app-modal
      ref="modal"
      size="medium"
      title="Filter Tanggal">
      <div class="flex max-sm:flex-col sm:justify-between gap-6">
        <div class="shortcuts">
          <app-button @click="shortcutToday()">
            Hari Ini
          </app-button>
          <app-button @click="shortcutThisMonth()">
            Bulan Ini
          </app-button>
          <app-button @click="shortcutThisYear()">
            Tahun Ini
          </app-button>
          <app-button @click="shortcutLast30Days()">
            ~30 hari
          </app-button>
          <app-button @click="shortcutLast3Months()">
            ~3 bulan
          </app-button>
          <app-button @click="shortcutLast6Months()">
            ~6 bulan
          </app-button>
          <app-button @click="shortcutLast12Months()">
            ~12 bulan
          </app-button>
        </div>
        <div class="content">
          <datepicker-navigator
            v-model:view="view"
            v-model:month="month"
            v-model:year="year" />

          <view-months-in-year
            v-if="view === 'months'"
            v-model:view="view"
            v-model:month="month"
            :year="year" />
          <view-days-in-month
            v-else-if="view === 'days'"
            :month="month"
            class="sm:text-sm!"
            :year="year">
            <template #default="{ date, gridColumnStart }">
              <app-button
                :style="{gridColumnStart}"
                :class="{
                  'selected-start': modelDate1 && isSameDay(date, modelDate1),
                  'selected-end': (modelDate1 && isSameDay(date, modelDate1) && modelDate2 === null) || (modelDate2 && isSameDay(date, modelDate2)),
                  'selected-mid': modelDate1 && modelDate2 && isAfter(date, modelDate1) && isBefore(date, modelDate2),
                }"
                @click="dateClicked(date)">
                {{ date.getDate() }}
              </app-button>
            </template>
          </view-days-in-month>

          <div
            v-if="stringifiedModel"
            class="text-xs sm:text-sm mt-4 flex-center h-[var(--h-input)] rounded">
            {{ stringifiedModel }}
          </div>

          <div class="flex justify-center gap-4">
            <div class="flex rounded border gap-1 items-center h-[var(--h-input)] px-3 text-sm">
              <select
                v-model="hour1"
                class="appearance-none">
                <option
                  v-for="i in 24"
                  :key="i"
                  :value="i-1">
                  {{ String(i-1).padStart(2, '0') }}
                </option>
              </select>
              :
              <select
                v-model="minute1"
                class="appearance-none">
                <option
                  v-for="i in 60"
                  :key="i"
                  :value="i-1">
                  {{ String(i-1).padStart(2, '0') }}
                </option>
              </select>
            </div>
            <div class="flex rounded border gap-1 items-center h-[var(--h-input)] px-3 text-sm">
              <select
                v-model="hour2"
                class="appearance-none">
                <option
                  v-for="i in 24"
                  :key="i"
                  :value="i-1">
                  {{ String(i-1).padStart(2, '0') }}
                </option>
              </select>
              :
              <select
                v-model="minute2"
                class="appearance-none">
                <option
                  v-for="i in 60"
                  :key="i"
                  :value="i-1">
                  {{ String(i-1).padStart(2, '0') }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <app-modal-footer>
        <app-button
          class="btn-gray-outline mr-auto"
          @click="reset()">
          Reset
        </app-button>
        <app-button
          class="btn-gray-outline"
          @click="close()">
          Tutup
        </app-button>
        <app-button
          class="btn-primary"
          @click="confirm()">
          Pilih
        </app-button>
      </app-modal-footer>
    </app-modal>
  </div>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  .content {
    @apply text-center w-full sm:w-80;
  }

  .selected-mid,
  .selected-start,
  .selected-end {
    @apply text-white bg-primary-500;
  }
  .selected-start {
    @apply rounded-l-full;
  }
  .selected-end {
    @apply rounded-r-full;
  }

  .shortcuts {
    @apply gap-2 sm:w-36 flex sm:flex-col overflow-auto scrollbar-none;
    button {
      @apply border rounded py-1 sm:py-1.5 px-2 text-xs sm:text-sm block shrink-0 sm:w-full;
    }
  }
</style>
